import React, {useEffect, useState} from 'react';
import { motion } from 'framer-motion';
import { Database, FileCode2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";

const mockSchema = [
  { table: 'Students', columns: ['Id (int)', 'Name (varchar)', 'Email (varchar)', 'EnrollmentDate (date)'] },
  { table: 'Courses', columns: ['Id (int)', 'Title (varchar)', 'Duration (int)', 'Level (varchar)'] },
  { table: 'Enrollments', columns: ['Id (int)', 'StudentId (int)', 'CourseId (int)', 'EnrollDate (date)'] }
];

const DbFirstTab = ({ state }) => {
  const { setCsharpOutput, setDbContextOutput, setSqlOutput } = state;

  const [scaffolded, setScaffolded] = useState(false);
  const [models, setModels] = useState([]);

  const mapSqlType = (t) => {
    const x = (t || "").toLowerCase();
    if (x.includes("int")) return "INT";
    if (x.includes("date")) return "DATE";
    if (x.includes("datetime")) return "DATETIME2";
    if (x.includes("varchar")) return "NVARCHAR(200)";
    return "NVARCHAR(MAX)";
  };

  const parseColumn = (col) => {
    // "Name (varchar)" -> { name: "Name", type: "varchar" }
    const m = col.match(/^(.+?)\s*\((.+?)\)$/);
    const name = (m?.[1] ?? col).trim();
    const type = (m?.[2] ?? "varchar").trim();
    return { name, type };
  };

  const buildCreateTableSql = (schema) => {
    const table = schema.table;
    const cols = schema.columns.map(parseColumn);

    const lines = cols.map(({ name, type }) => {
      if (name.toLowerCase() === "id") {
        return `  [Id] INT IDENTITY(1,1) NOT NULL`;
      }
      // for demo: make FK columns NOT NULL, others NULL
      const isFk = name.toLowerCase().endsWith("id") && name.toLowerCase() !== "id";
      return `  [${name}] ${mapSqlType(type)} ${isFk ? "NOT NULL" : "NULL"}`;
    });

    const constraints = [`  CONSTRAINT [PK_${table}] PRIMARY KEY ([Id])`];

    // Simple demo FKs for Enrollments
    if (table === "Enrollments") {
      constraints.push(
          `  CONSTRAINT [FK_Enrollments_Students] FOREIGN KEY ([StudentId]) REFERENCES [Students]([Id])`,
          `  CONSTRAINT [FK_Enrollments_Courses] FOREIGN KEY ([CourseId]) REFERENCES [Courses]([Id])`
      );
    }

    return (
        `CREATE TABLE [${table}] (\n` +
        `${lines.join(",\n")},\n` +
        `${constraints.join(",\n")}\n` +
        `);\n`
    );
  };

  const scaffoldModels = () => {
    const newModels = mockSchema.map((table) => ({
      name: table.table,
      properties: table.columns,
    }));

    setModels(newModels);

    // DbContext snapshot (existing)
    const dbSets = newModels
        .map((m) => `  public DbSet<${m.name}> ${m.name}s { get; set; }`)
        .join("\n");

    setDbContextOutput(
        `public partial class ApplicationDbContext : DbContext\n{\n${dbSets}\n\n  protected override void OnModelCreating(ModelBuilder modelBuilder)\n  {\n    base.OnModelCreating(modelBuilder);\n    // Generated configuration would appear here\n  }\n}`
    );

    const createTablesSql =
        `-- Illustrative SQL (schema-first)\n` +
        `-- These are example CREATE TABLE statements based on the detected schema.\n\n` +
        mockSchema.map(buildCreateTableSql).join("\n");

    setSqlOutput(createTablesSql);

    // Keep scaffold command in C# EXAMPLE (your existing intent)
    setCsharpOutput(
        `// Scaffold command (Database-First)\n` +
        `dotnet ef dbcontext scaffold "ConnectionString" Microsoft.EntityFrameworkCore.SqlServer\n`
    );

    setScaffolded(true);
    toast.success("Models scaffolded from database schema");
  };

  return (
    <div className="space-y-6 pt-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold">Database-First Approach</h3>
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>• Start with an existing database</li>
          <li>• Scaffold entity models from schema</li>
          <li>• Generate DbContext automatically</li>
        </ul>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold">Existing Database Schema</h4>
        <div className="space-y-2">
          {mockSchema.map((schema, index) => (
            <Card key={index} className="bg-surface-elevated">
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <Database className="mt-0.5 h-4 w-4 text-primary" />
                  <div className="flex-1">
                    <p className="font-medium">{schema.table}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {schema.columns.map((col, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {col}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Button className="w-full" onClick={scaffoldModels} disabled={scaffolded}>
          <FileCode2 className="mr-2 h-4 w-4" />
          {scaffolded ? 'Models Scaffolded' : 'Scaffold Models'}
        </Button>
      </div>

      {scaffolded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-2"
        >
          <h4 className="text-sm font-semibold">Generated Models</h4>
          <div className="space-y-2">
            {models.map((model, index) => (
                <Dialog key={index}>
                  <DialogTrigger asChild>
                    <Card className="cursor-pointer bg-surface-elevated border-primary/20 hover:border-primary/40">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-2">
                          <FileCode2 className="h-4 w-4 text-primary" />
                          <span className="font-medium">{model.name}.cs</span>
                          <Badge variant="secondary" className="ml-auto text-xs">Generated</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </DialogTrigger>

                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{model.name} Model (Scaffolded Example)</DialogTitle>
                      <DialogDescription>
                        Demo representation of what scaffolding could generate.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-lg border border-border bg-surface-elevated p-4">
        <pre className="overflow-x-auto text-sm leading-relaxed">
          <code className="text-foreground">
{`public partial class ${model.name}
{
${model.properties.map((p) => `  public string ${p.split(" ")[0]} { get; set; }`).join("\n")}
}`}
          </code>
        </pre>
                    </div>
                  </DialogContent>
                </Dialog>
            ))}
          </div>
          <div className="rounded-md bg-surface-elevated border border-border p-3 text-sm">
            <p className="mb-1 text-xs font-medium text-muted-foreground">Generated DbContext</p>
            <code className="text-foreground">ApplicationDbContext.cs</code>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default DbFirstTab;
